/**
 * Player (phone) end-game view for Scotland Yard.
 *
 * Compact, scroll-friendly summary suitable for a phone screen:
 *   - Big winner banner.
 *   - "You were [Detective/Mr. X]" identity card.
 *   - Mr. X's full revealed path as a vertical list (round + transport icon
 *     + station number).
 *   - "Play again" link back to the create-room flow.
 *
 * No map / scrubber on phone for v1 — too much UI for the screen, and the
 * host TV already shows the rich version. Phone players get the data
 * payoff (the path) without the spatial visualization.
 *
 * Graceful degradation: if `state.public.replay` is missing (legacy game),
 * we still render the winner + identity, and fall back to showing only the
 * already-revealed reveal-round stations from `mrx.transportLog`.
 */

import { Badge, Box, Button, Group, Paper, ScrollArea, Stack, Text, ThemeIcon, Title } from '@mantine/core'
import { Link } from 'react-router-dom'
import { PLAYER_COLOR_HEX } from '@mdev/common'
import type { Room, GameState, MrxLogEntry } from '@mdev/common'
import type { ScotlandYardSettings } from '../module'
import { SY, SY_ACCENT_YELLOW, SY_WIN_GREEN } from '../theme'
import { TRANSPORT_COLOR, TRANSPORT_LABEL, TransportIcon } from './TransportIcon'
import { stationColor } from './stationTransports'

interface Props {
  room: Room<ScotlandYardSettings>
  state: { public: GameState; mrxStation: number }
  uid: string
}

export function PlayerFinishedView( props: Props ) {
  const { state, uid } = props
  const publicState = state.public
  const winner = publicState.winner

  const isMrX = uid === publicState.mrx.uid
  const detective = publicState.detectives.find( ( d ) => d.uid === uid )

  // Index 0 of mrxStations is the starting position; index k+1 corresponds
  // to mrx.transportLog[k]. Treat a missing replay as null and fall back
  // to the live-reveal-only stations on each log entry.
  const mrxStations = publicState.replay?.mrxStations ?? null

  const headlineColor = winner?.team === 'detectives' ? SY_WIN_GREEN : SY_ACCENT_YELLOW
  const headline = winner
    ? winner.team === 'detectives'
      ? 'Detectives win!'
      : 'Mr. X escapes!'
    : 'Game over'

  return (
    <ScrollArea
      style={ { height: '100vh', background: SY.pageBg } }
    >
      <Stack gap="md" p="md" style={ { minHeight: '100vh' } }>
        <Paper
          p="lg"
          radius="lg"
          style={ {
            background: SY.panelBg,
            border: `2px solid ${ headlineColor }`,
            textAlign: 'center',
          } }
        >
          <Title order={ 1 } style={ { color: headlineColor, fontWeight: 900, fontSize: 32 } }>
            { headline }
          </Title>
          { winner && (
            <Text c="dimmed" size="sm" mt={ 4 }>
              { humanReason( winner.reason, publicState ) }
            </Text>
          ) }
        </Paper>

        <IdentityCard
          isMrX={ isMrX }
          mrxAvatar={ publicState.mrx.avatar }
          mrxName={ publicState.mrx.displayName }
          detective={ detective ?? null }
        />

        <MrxPathPanel state={ publicState } mrxStations={ mrxStations } />

        { props.room.nextCode ? (
          <Button
            component={ Link }
            to={ `/games/${ props.room.gameId }/play/${ props.room.nextCode }/new` }
            color="yellow"
            size="lg"
            fullWidth
          >
            Join next game →
          </Button>
        ) : (
          <Button
            color="gray"
            variant="light"
            size="lg"
            fullWidth
            disabled
          >
            Waiting for the host to start a new game…
          </Button>
        ) }
        <Button
          component={ Link }
          to="/games"
          variant="light"
          fullWidth
        >
          Back to games
        </Button>
      </Stack>
    </ScrollArea>
  )
}

function IdentityCard( props: {
  isMrX: boolean
  mrxAvatar: string
  mrxName: string
  detective: { displayName: string; avatar: string; color: keyof typeof PLAYER_COLOR_HEX } | null
} ) {
  const { isMrX, mrxAvatar, mrxName, detective } = props
  if ( isMrX ) {
    return (
      <Paper p="md" radius="md" style={ { background: SY.surfaceBg, border: `1px solid ${ SY.border }` } }>
        <Group gap="md" wrap="nowrap">
          <Box
            style={ {
              width: 56, height: 56, borderRadius: '50%', background: SY.border,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30,
            } }
          >
            { mrxAvatar }
          </Box>
          <Stack gap={ 0 }>
            <Text c="dimmed" size="xs" tt="uppercase" fw={ 700 }>You were</Text>
            <Text c="bright" fw={ 700 } size="lg">Mr. X — { mrxName }</Text>
          </Stack>
        </Group>
      </Paper>
    )
  }
  if ( !detective ) {
    return (
      <Paper p="md" radius="md" style={ { background: SY.surfaceBg, border: `1px solid ${ SY.border }` } }>
        <Text c="dimmed">You were a spectator.</Text>
      </Paper>
    )
  }
  return (
    <Paper p="md" radius="md" style={ { background: SY.surfaceBg, border: `1px solid ${ SY.border }` } }>
      <Group gap="md" wrap="nowrap">
        <Box
          style={ {
            width: 56, height: 56, borderRadius: '50%',
            background: PLAYER_COLOR_HEX[ detective.color ],
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30,
          } }
        >
          { detective.avatar }
        </Box>
        <Stack gap={ 0 }>
          <Text c="dimmed" size="xs" tt="uppercase" fw={ 700 }>You were</Text>
          <Text c="bright" fw={ 700 } size="lg">Detective { detective.displayName }</Text>
        </Stack>
      </Group>
    </Paper>
  )
}

function MrxPathPanel( props: { state: GameState; mrxStations: number[] | null } ) {
  const { state, mrxStations } = props
  const log = state.mrx.transportLog

  return (
    <Paper p="md" radius="md" style={ { background: SY.surfaceBg, border: `1px solid ${ SY.border }` } }>
      <Group justify="space-between" align="baseline" mb="sm">
        <Title order={ 4 } c="bright">Mr. X's path</Title>
        <Text size="xs" c="dimmed">All revealed</Text>
      </Group>

      { mrxStations && mrxStations.length > 0 && (
        <Group gap="xs" wrap="nowrap" mb="sm">
          <Badge size="sm" color="gray" variant="light">Start</Badge>
          <Text size="sm" c="bright" fw={ 600 }>#{ mrxStations[ 0 ] }</Text>
        </Group>
      ) }

      <Stack gap={ 6 }>
        { log.map( ( entry, idx ) => {
          const station = mrxStations ? mrxStations[ idx + 1 ] ?? null : entry.revealedStation
          return <PathRow key={ idx } entry={ entry } station={ station } />
        } ) }
        { log.length === 0 && (
          <Text size="sm" c="dimmed">No moves recorded.</Text>
        ) }
      </Stack>
    </Paper>
  )
}

function PathRow( { entry, station }: { entry: MrxLogEntry; station: number | null } ) {
  return (
    <Group
      gap="xs"
      wrap="nowrap"
      style={ {
        background: SY.revealRowBg,
        border: `1px solid ${ SY.border }`,
        borderRadius: 6,
        padding: '6px 10px',
      } }
    >
      <Text size="sm" c="dimmed" w={ 26 } fw={ 700 }>
        { entry.round }
      </Text>
      <ThemeIcon size="md" variant="transparent">
        <TransportIcon transport={ entry.transport } size={ 20 } />
      </ThemeIcon>
      <Text size="sm" c={ TRANSPORT_COLOR[ entry.transport ] } flex={ 1 }>
        { TRANSPORT_LABEL[ entry.transport ] }{ entry.doubleMoveSecond ? ' (2×)' : '' }
      </Text>
      { station != null ? (
        <Badge size="md" variant="filled" style={ { background: stationColor( station ), color: '#fff' } }>#{ station }</Badge>
      ) : (
        <Badge size="md" color="gray" variant="light">?</Badge>
      ) }
    </Group>
  )
}

function humanReason( reason: string, state: GameState ): string {
  switch ( reason ) {
    case 'capture': {
      const station = state.replay
        ? state.replay.mrxStations[ state.replay.mrxStations.length - 1 ]
        : state.mrx.lastRevealedStation
      return station != null
        ? `Captured at #${ station } in round ${ state.round }`
        : `Captured in round ${ state.round }`
    }
    case 'mrx_cornered':
      return 'Mr. X had no legal moves left'
    case 'all_detectives_stranded':
      return 'Every detective ran out of legal moves'
    case 'survived_24_rounds':
      return `Mr. X survived all ${ state.round } rounds`
    default:
      return reason
  }
}
